import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, ImageOff, Flag, UserX, RefreshCcw, UserCheck } from 'lucide-react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const TYPE_META = {
  pii_flag: { label: 'Contact details', icon: ShieldAlert, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  image_report: { label: 'Reported image', icon: Flag, tone: 'bg-red-50 text-red-700 border-red-200' },
  blocked_upload: { label: 'Blocked upload', icon: ImageOff, tone: 'bg-stone-100 text-stone-700 border-stone-200' },
};
const FILTERS = [['', 'All'], ['pii_flag', 'Contact details'], ['image_report', 'Reported images'], ['blocked_upload', 'Blocked uploads']];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function QueueItem({ item, onSuspend }) {
  const meta = TYPE_META[item.type];
  const Icon = meta.icon;
  const s = item.sender || {};
  return (
    <li className="py-4 border-b border-[#E6E4DD] last:border-0 flex flex-col md:flex-row md:items-start gap-4" data-testid={`moderation-item-${item.item_id}`}>
      <span className={`inline-flex items-center gap-1.5 shrink-0 text-xs px-2 py-1 rounded border ${meta.tone}`}>
        <Icon className="h-3.5 w-3.5" />{meta.label}
      </span>
      <div className="flex-1 min-w-0 text-sm">
        <p className="text-[#1A1A1A] font-medium">
          {s.name || 'Unknown user'} <span className="text-stone-400 font-normal">{s.email} · {s.role}</span>
          {s.suspended && <span className="ml-2 text-xs text-red-600" data-testid="moderation-item-suspended-badge">Suspended</span>}
        </p>
        <p className="text-stone-600 mt-1 break-words">{item.summary}</p>
        {item.original_content && (
          <p className="text-xs text-stone-500 mt-1 break-words">Sent: “{item.original_content}”</p>
        )}
        {item.image_url && (
          <a href={item.image_url} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] underline">Open image</a>
        )}
        <p className="text-xs text-stone-400 mt-1">{fmt(item.created_at)}{item.strike_count ? ` · strike ${item.strike_count}` : ''}</p>
      </div>
      {!s.suspended && s.role !== 'admin' && s.user_id && (
        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 shrink-0"
          onClick={() => onSuspend(s)} data-testid={`moderation-suspend-btn-${item.item_id}`}>
          <UserX className="h-4 w-4 mr-1.5" />Suspend sender
        </Button>
      )}
    </li>
  );
}

function SuspendDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.user_id}/suspend`, { reason });
      toast.success(`${target.name || 'User'} suspended and signed out`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not suspend this user');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="suspend-dialog">
        <DialogHeader>
          <DialogTitle>Suspend {target?.name || 'this user'}?</DialogTitle>
          <DialogDescription>They are signed out everywhere and cannot log in until an admin restores them.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (kept for the audit trail)" data-testid="suspend-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="suspend-cancel-btn">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submit} disabled={busy} data-testid="suspend-confirm-btn">Suspend</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuspendedList({ users, onRestore }) {
  if (!users.length) return null;
  return (
    <div className="card-surface p-6 mt-6" data-testid="suspended-users-list">
      <h3 className="font-heading text-lg text-[#1A1A1A] mb-3">Suspended accounts</h3>
      <ul>
        {users.map((u) => (
          <li key={u.user_id} className="flex items-center justify-between gap-4 py-3 border-b border-[#E6E4DD] last:border-0 text-sm">
            <div className="min-w-0">
              <p className="font-medium text-[#1A1A1A]">{u.name} <span className="text-stone-400 font-normal">{u.email}</span></p>
              <p className="text-xs text-stone-500">{fmt(u.suspended_at)}{u.suspended_reason ? ` · ${u.suspended_reason}` : ''}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => onRestore(u)} data-testid={`unsuspend-btn-${u.user_id}`}>
              <UserCheck className="h-4 w-4 mr-1.5" />Restore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminModerationTab() {
  const [filter, setFilter] = useState('');
  const [data, setData] = useState(null);
  const [suspended, setSuspended] = useState([]);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([
        api.get('/admin/moderation/queue', { params: filter ? { type: filter } : {} }),
        api.get('/admin/moderation/suspended'),
      ]);
      setData(q.data);
      setSuspended(s.data.users || []);
    } catch {
      toast.error('Could not load the moderation queue');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const restore = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/unsuspend`);
      toast.success(`${u.name || 'User'} restored`);
      load();
    } catch { toast.error('Could not restore this user'); }
  };

  const counts = data?.counts || {};
  return (
    <div data-testid="admin-moderation-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[['pii_flag', 'Contact details'], ['image_report', 'Reported images'], ['blocked_upload', 'Blocked uploads'], ['suspended_users', 'Suspended']].map(([k, label]) => (
          <div key={k} className="card-surface p-4" data-testid={`moderation-count-${k}`}>
            <p className="text-xs text-stone-500">{label}</p>
            <p className="font-heading text-2xl text-[#1A1A1A]">{counts[k] ?? '-'}</p>
          </div>
        ))}
      </div>
      <div className="card-surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(([key, label]) => (
              <button key={key || 'all'} onClick={() => setFilter(key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === key ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-stone-400'}`}
                data-testid={`moderation-filter-${key || 'all'}`}>{label}</button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="moderation-refresh-btn"><RefreshCcw className="h-4 w-4 mr-1.5" />Refresh</Button>
        </div>
        {!data ? (
          <p className="text-sm text-stone-400 py-8 text-center">Loading...</p>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-stone-500 py-8 text-center" data-testid="moderation-empty">Nothing flagged right now.</p>
        ) : (
          <ul data-testid="moderation-queue-list">
            {data.items.map((item) => <QueueItem key={item.item_id} item={item} onSuspend={setTarget} />)}
          </ul>
        )}
      </div>
      <SuspendedList users={suspended} onRestore={restore} />
      <SuspendDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
}
